import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { motionAllowed } from "@/motion/MotionProvider";
import { Menu, X } from "lucide-react";
import { SITE } from "@/config";
import { usePremiumButton } from "@/motion/hooks";

const links = [
    { label: "Work", href: "#work", testId: "nav-link-work" },
    { label: "About", href: "#about", testId: "nav-link-about" },
    { label: "Services", href: "#services", testId: "nav-link-services" },
    { label: "Contact", href: "#contact", testId: "nav-link-contact" },
];

export default function Nav() {
    const navCta = usePremiumButton("fill");
    const headerRef = useRef(null);
    useEffect(() => {
        if (!headerRef.current || !motionAllowed()) return;
        gsap.fromTo(headerRef.current, { opacity: 0, y: -12 },
            { opacity: 1, y: 0, duration: 0.6, delay: 0.05, ease: "luxe" });
    }, []);
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            ref={headerRef}
            data-testid="site-nav"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-[#030303]/80 backdrop-blur-xl border-b border-[color:var(--line)]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 md:px-10 py-5">
                <a href="#top" data-testid="nav-logo" className="flex flex-col leading-none">
                    <span className="font-display text-2xl md:text-[1.7rem] tracking-wide text-[color:var(--fg)]">
                        Nikhil Joshi
                    </span>
                    <span className="text-[10px] uppercase tracking-luxe text-[color:var(--gold)] mt-1">
                        {SITE.handle}
                    </span>
                </a>

                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={l.testId}
                            className="link-gold text-xs uppercase tracking-[0.22em] text-[color:var(--fg)]/80 hover:text-[color:var(--fg)]"
                        >
                            {l.label}
                        </a>
                    ))}
                    <a
                        ref={navCta}
                        href="#contact"
                        data-testid="nav-cta-work"
                        className="border border-[color:var(--gold)] px-6 py-2.5 text-[11px] uppercase tracking-[0.22em] text-[color:var(--gold)] will-change-transform"
                    >
                        Work With Me
                    </a>
                </nav>

                <button
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Menu"
                    className="md:hidden text-[color:var(--fg)]"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

                            {open && (
                    <div
                        data-testid="nav-mobile-menu"
                        className="md:hidden border-t border-[color:var(--line)] bg-[#030303]/95 backdrop-blur-xl"
                    >
                        <div className="flex flex-col px-6 py-8 gap-6">
                            {links.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    data-testid={`${l.testId}-mobile`}
                                    className="font-display text-3xl text-[color:var(--fg)]"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href="#contact"
                                onClick={() => setOpen(false)}
                                data-testid="nav-cta-work-mobile"
                                className="mt-2 inline-block border border-[color:var(--gold)] px-6 py-3 text-xs uppercase tracking-[0.22em] text-[color:var(--gold)] w-max"
                            >
                                Work With Me
                            </a>
                        </div>
                    </div>
                )}
        </header>
    );
}
